import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { BLOG_POSTS, AUTHOR } from "../content/blogPosts";
import { ArrowRight, Clock, BookOpen } from "lucide-react";

const SITE = "https://www.thecognitivemetrics.com";

export const Blog = () => {
  useEffect(() => {
    document.title = "IQ & Cognitive Science Blog · The Cognitive Metrics";
    const el = document.querySelector('meta[name="description"]');
    if (el) el.setAttribute("content", "Casual, science-backed guides on IQ scores, fluid intelligence, brain training and how to measure and improve your reasoning.");
    const link = document.querySelector('link[rel="canonical"]');
    if (link) link.setAttribute("href", `${SITE}/blog`);
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F17] grain relative" data-testid="blog-index">
      <Header />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-[120px]" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-6 py-12 sm:py-16">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-5">
            <BookOpen className="w-3.5 h-3.5 text-cyan-400" />
            <span className="uppercase tracking-[0.25em] text-[11px] font-semibold text-cyan-400">The Journal</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">IQ & cognitive science, made readable</h1>
          <p className="mt-4 text-slate-400 max-w-2xl text-base sm:text-lg leading-relaxed">
            Plain-English guides on IQ scores, fluid intelligence and how to actually sharpen your reasoning — no jargon, no gatekeeping.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-10">
          {BLOG_POSTS.map((p, i) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group rounded-3xl overflow-hidden border border-white/10 bg-[#131B2A] hover:border-cyan-500/40 transition-all fade-up"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <div className="relative h-44 overflow-hidden">
                <img src={p.heroImage} alt={p.heroAlt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131B2A] to-transparent" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-xs text-slate-500 font-mono-data mb-2">
                  <span>{new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.readTime}</span>
                </div>
                <h2 className="font-heading text-xl font-bold text-white leading-snug group-hover:text-cyan-300 transition-colors">{p.title}</h2>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{p.excerpt}</p>
                <p className="text-xs text-slate-500 mt-3">By {AUTHOR.name}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-400">
                  Read article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-14 p-8 rounded-3xl border border-amber-500/30 bg-gradient-to-b from-[#182234] to-[#0F172A] text-center fade-up">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Enough reading — find out your number</h2>
          <p className="mt-3 text-slate-400 max-w-xl mx-auto">Take the credited 30-question IQ test and get your exact score, global percentile and a full cognitive breakdown.</p>
          <Link to="/" data-testid="blog-cta-test" className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold px-6 py-3.5 rounded-xl hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] transition-all">
            Take the IQ test <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};
